import { MinusIcon, PlusIcon } from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import CurrencyDisplay from "src/components/atoms/currency-display/currency-display";
import Divider from "src/components/atoms/divider/divider";
import DropdownBox, {
	IDropdownOptionProp,
} from "src/components/atoms/dropdown/dropdown-box";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";
import BoxWithIcon from "src/components/molecules/box-with-icon/box-with-icon";
import CopyRight from "src/components/molecules/copy-right/CopyRight";
import Logo from "src/components/molecules/logo/Logo";
import { ReactComponent as DollarIcon } from "../../../../assets/svg/dollarIcon.svg";
import { ReactComponent as OutgoingCallIcon } from "../../../../assets/svg/outgoingCallIcon.svg";
import { ReactComponent as IncomingCallIcon } from "../../../../assets/svg/incomingCallIcon.svg";
import { ReactComponent as InformationCallIcon } from "../../../../assets/svg/informationIcon.svg";
import NumberDisplay from "src/components/atoms/number-display/number-display";
import Modal from "src/components/molecules/modal/modal";
import CheckBox from "src/components/molecules/check-box/check-box";
import OTPInput from "src/components/molecules/otp-input/otp-input";
import HyperLink from "src/components/atoms/hyper-link/hyper-link";
import ButtonPro from "src/components/atoms/primary-button/primary-button";
import { PricingCalculatorViewModel } from "./pricing-calculator-view-model";
import LoadingModal from "src/components/molecules/loading/LoadingModal";
import Alert from "src/components/atoms/alert/alert";
import { set } from "date-fns";
import CountdownTimer from "src/components/molecules/count-down-timer/count-down-timer";

interface IBrandingBreakdown {
	tier: string;
	description: string;
	calls: number;
	cost: string;
}

interface ICallBreakdown {
	tier: string;
	minutes: number;
	cost: string;
	description: string;
	direction: string;
}

interface IUserDetails {
	firstName: string;
	lastName: string;
	emailId: string;
	emailConfirmed: boolean;
	termsAgreed: boolean;
}

const PartnerPricingCalculator = () => {
	const [showResendOtpButton, setShowResendOtpButton] = useState(false);
	const [otpError, setOtpError] = useState("");
	const [loading, setLoading] = useState(false);
	const [user, setUser] = useState<IUserDetails>();
	const [userFirstName, setUserFirstName] = useState("");
	const [userLastName, setUserLastName] = useState("");
	const [userEmailAddress, setUserEmailAddress] = useState("");
	const [userEmailConfirmed, setUserEmailConfirmed] = useState(false);
	const [userTermsAccepted, setUserTermsAccepted] = useState(false);
	const [isEmailValid, setIsEmailValid] = useState<boolean | undefined>(
		undefined
	);
	const [showOtpEntryBox, setShowOtpEntryBox] = useState(false);
	const [showUnAuthenticatedUser, setShowUnAuthenticatedUser] =
		useState(false);
	const [otpConfirmButtonDisabled, setOtpConfirmButtonDisabled] =
		useState(true);
	const [otp, setOtp] = useState("");
	const [platformCost, setPlatformCost] = useState(2000);
	const [platformCost247, setPlatformCost247] = useState(4000);
	const [supportNeeded247, setSupportNeeded247] = useState(false);
	const [numOutgoingCalls, setNumOutgoingCalls] = useState(0);
	const [avgOutgoingCallLength, setAvgOutgoingCallLength] = useState(0);
	const [numIncomingCalls, setNumIncomingCalls] = useState(0);
	const [avgIncomingCallLength, setAvgIncomingCallLength] = useState(0);
	const [showDisclaimer, setShowDisclaimer] = useState(false);
	const [brandingBreakdown, setBrandingBreakdown] = useState<
		IBrandingBreakdown[]
	>([]);
	const [isBrandingExpanded, setIsBrandingExpanded] = useState(false);
	const [
		isIncomingCallCostBreakdownExpanded,
		setIsIncomingCallCostBreakdownExpanded,
	] = useState(false);
	const [
		isOutgoingCallCostBreakdownExpanded,
		setIsOutgoingCallCostBreakdownExpanded,
	] = useState(false);
	const [callBreakdown, setCallBreakdown] = useState<ICallBreakdown[]>([]);
	const [brandingCost, setBrandingCost] = useState(0);
	const [incomingCallCost, setIncomingCallCost] = useState(0);
	const [outgoingCallCost, setOutgoingCallCost] = useState(0);
	const [monthlyCost, setMonthlyCost] = useState(0);
	const [applyingLocalRates, setApplicationLocalRates] = useState(true);
	const [localRate, setLocalRate] = useState(1);
	const [conversionRate, setConversionRate] = useState(1);
	const [
		percentageOfMobileTo1300CallMinutes,
		setPercentageOfMobileTo1300CallMinutes,
	] = useState(70);
	const [percentageOfCallToMobile, setPercentageOfCallToMobile] =
		useState(90);
	const [percentageOfCallToLandline, setPercentageOfCallToLandline] =
		useState(10);
	const [mobileTo1300CallRates, setMobileTo1300CallRates] = useState(0.08);
	const [landlineTo1300CallRates, setLandlineTo1300CallRates] =
		useState(0.02);
	const [callRatesToMobile, setCallRatesToMobile] = useState(0.05);
	const [callRatesToLandline, setCallRatesToLandline] = useState(0.01);
	const [
		mobileTo1300CallRatesInputValue,
		setMobileTo1300CallRatesInputValue,
	] = useState<string>(""); // Temporary state to store input
	const [callRatesToMobileInputValue, setCallRatesToMobileInputValue] =
		useState<string>(""); // Temporary state to store input
	const [callRatesToLandlineInputValue, setCallRatesToLandlineInputValue] =
		useState<string>(""); // Temporary state to store input
	const [
		landlineTo1300CallRatesInputValue,
		setLandlineTo1300CallRatesInputValue,
	] = useState<string>(""); // Temporary state to store input

	const [
		percentageOfLandlineTo1300CallMinutes,
		setPercentageOfLandlineTo1300CallMinutes,
	] = useState(30);

	const [resellerMarginMultiplier, setResellerMarginMultiplier] =
		useState(0.7);
	const [monthlyCostForSIPAndInfra, setMonthlyCostForSIPAndInfra] =
		useState(200);
	const [monthlyChannelCost, setMonthlyChannelCost] = useState(500);

	const viewModel = useRef<PricingCalculatorViewModel | null>(null);
	// Branding Pricing Tiers (Applies only to outgoing calls)
	const brandingTiers = [
		{
			limit: 100000,
			description: "1-100000",
			pricePerCall: 0.035,
			tier: "Pricing Tier 1",
		},
		{
			limit: 400000,
			description: "100001-400000",
			pricePerCall: 0.025,
			tier: "Pricing Tier 2",
		},
		{
			limit: 500000,
			description: "400001-1000000",
			pricePerCall: 0.015,
			tier: "Pricing Tier 3",
		},
		{
			limit: Infinity,
			description: "1000000-infinity",
			pricePerCall: 0,
			tier: "Pricing Tier 4",
		},
	];

	// Call Pricing Tiers (Infra + Service Provider Cost)
	const callTiers = [
		{
			limit: 100000,
			infra: 0.0079,
			spCallCostPerMin: 0.0079,
			scCallCostMultiplier: 2,
			tier: "Pricing Tier 1",
			description: "1-100000",
		},
		{
			limit: 400000,
			infra: 0.0079,
			spCallCostPerMin: 0.0079,
			scCallCostMultiplier: 1.8,
			tier: "Pricing Tier 2",
			description: "100001-400000",
		},
		{
			limit: 500000,
			infra: 0.0079,
			spCallCostPerMin: 0.0079,
			scCallCostMultiplier: 1.5,
			tier: "Pricing Tier 3",
			description: "400001-1000000",
		},
		{
			limit: Infinity,
			infra: 0.0079,
			spCallCostPerMin: 0.0079,
			scCallCostMultiplier: 1.8,
			tier: "Pricing Tier 4",
			description: "1000000-infinity",
		},
	];

	useEffect(() => {
		viewModel.current = new PricingCalculatorViewModel();
		const userInStorage = sessionStorage.getItem("sc_calculator_user");

		if (userInStorage) {
			const userObject: IUserDetails = JSON.parse(userInStorage);
			setUserFirstName(userObject.firstName);
			setUserLastName(userObject.lastName);
			setUserEmailAddress(userObject.emailId);
			setUserTermsAccepted(userObject.termsAgreed);
			setUser(userObject);
		}

		getExchangeRate();
		setLandlineTo1300CallRatesInputValue("0.02");
		setMobileTo1300CallRatesInputValue("0.08");
		setLandlineTo1300CallRates(0.02);
		setMobileTo1300CallRates(0.08);

		setCallRatesToLandlineInputValue("0.01");
		setCallRatesToMobileInputValue("0.05");
		setCallRatesToLandline(0.01);
		setCallRatesToMobile(0.05);
	}, []);

	useEffect(() => {
		if (supportNeeded247) {
			setPlatformCost(platformCost247);
		} else {
			setPlatformCost(2000);
		}
	}, [supportNeeded247]);

	useEffect(() => {
		calculateCost();
	}, [
		platformCost,
		numOutgoingCalls,
		avgOutgoingCallLength,
		numIncomingCalls,
		avgIncomingCallLength,
		localRate,
	]);

	const handleLandlineChange = (value: number) => {
		setPercentageOfLandlineTo1300CallMinutes(value);
		setPercentageOfMobileTo1300CallMinutes(100 - value);
	};

	const handleMobileChange = (value: number) => {
		setPercentageOfMobileTo1300CallMinutes(value);
		setPercentageOfLandlineTo1300CallMinutes(100 - value);
	};

	const handleOutboundLandlineChange = (value: number) => {
		setPercentageOfCallToLandline(value);
		setPercentageOfCallToMobile(100 - value);
	};

	const handleOutboundMobileChange = (value: number) => {
		setPercentageOfCallToMobile(value);
		setPercentageOfCallToLandline(100 - value);
	};

	const calculateCost = () => {
		// **Total Calls Calculation**
		const rate = localRate;

		// **Branding Cost Calculation**
		let remainingCalls = numOutgoingCalls;
		let brandingTotalCost = 0;
		const brandingBreakdown: IBrandingBreakdown[] = [];

		for (const brandingTier of brandingTiers) {
			if (remainingCalls <= 0) break;

			const callsInTier = Math.min(remainingCalls, brandingTier.limit);
			const cost = callsInTier * brandingTier.pricePerCall * rate; // Applying rate multiplier
			brandingTotalCost += cost;
			brandingBreakdown.push({
				tier: brandingTier.tier,
				description: brandingTier.description,
				calls: callsInTier,
				cost: cost.toFixed(5),
			});
			remainingCalls -= callsInTier;
		}

		// **Call Cost Calculation (Infra + Service Provider)**
		let remainingOutgoingMinutes = numOutgoingCalls * avgOutgoingCallLength;
		let remainingIncomingMinutes = numIncomingCalls * avgIncomingCallLength;
		let outgoingTotalCost = 0;
		let incomingTotalCost = 0;
		const callDetails: ICallBreakdown[] = [];

		for (const callingTier of callTiers) {
			if (remainingOutgoingMinutes <= 0 && remainingIncomingMinutes <= 0)
				break;

			// Calculate Outgoing Call Costs
			if (remainingOutgoingMinutes > 0) {
				const minutesInTier = Math.min(
					remainingOutgoingMinutes,
					callingTier.limit
				);
				const cost =
					minutesInTier *
					(callingTier.infra +
						callingTier.spCallCostPerMin *
							callingTier.scCallCostMultiplier) *
					rate; // Applying rate multiplier
				outgoingTotalCost += cost;
				callDetails.push({
					tier: `${callingTier.tier}`,
					minutes: minutesInTier,
					cost: cost.toFixed(4),
					description: `${callingTier.description}`,
					direction: "Outgoing",
				});
				remainingOutgoingMinutes -= minutesInTier;
			}

			// Calculate Incoming Call Costs
			if (remainingIncomingMinutes > 0) {
				const minutesInTier = Math.min(
					remainingIncomingMinutes,
					callingTier.limit
				);
				const cost =
					minutesInTier *
					(callingTier.infra +
						callingTier.spCallCostPerMin *
							callingTier.scCallCostMultiplier) *
					rate; // Applying rate multiplier
				incomingTotalCost += cost;
				callDetails.push({
					tier: `${callingTier.tier}`,
					minutes: minutesInTier,
					cost: cost.toFixed(4),
					description: `${callingTier.description}`,
					direction: "Incoming",
				});
				remainingIncomingMinutes -= minutesInTier;
			}
		}

		// **Compute Final Costs**
		const totalMonthlyCost =
			platformCost * rate +
			brandingTotalCost +
			outgoingTotalCost +
			incomingTotalCost;

		// **Set States**
		setMonthlyCost(totalMonthlyCost);
		setBrandingCost(brandingTotalCost);
		setIncomingCallCost(incomingTotalCost);
		setOutgoingCallCost(outgoingTotalCost);
		setBrandingBreakdown(brandingBreakdown);
		setCallBreakdown(callDetails);
	};

	const getExchangeRate = async () => {
		try {
			const response = await fetch(
				"https://api.frankfurter.app/latest?from=USD&to=AUD"
			);
			const data = await response.json();
			console.log(data.rates.AUD);
			setConversionRate(1);
			setApplicationLocalRates(true);
			setLocalRate(1);
		} catch (error) {
			console.error("Error fetching exchange rate:", error);
			setConversionRate(1);
			setApplicationLocalRates(false);
			setLocalRate(1);
		}
	};

	const toggleShowDisclaimer = () => {
		setShowDisclaimer((prevValue) => !prevValue);
	};

	const toggleShowOtpEntryBox = async () => {
		setShowOtpEntryBox((prevValue) => !prevValue);
	};

	const confirmUserEmail = async () => {
		const result = await viewModel.current?.verifyOtp(
			userEmailAddress,
			otp
		);

		if (result && result.result === "Valid") {
			setIsEmailValid(true);
			setUserEmailConfirmed(true);

			const user: IUserDetails = {
				firstName: userFirstName,
				lastName: userLastName,
				emailId: userEmailAddress,
				emailConfirmed: true,
				termsAgreed: userTermsAccepted,
			};
			setUser(user);
			sessionStorage.setItem("sc_calculator_user", JSON.stringify(user));
			toggleShowOtpEntryBox();
		} else if (result && result.result === "Invalid") {
			setIsEmailValid(false);
			setUserEmailConfirmed(false);
			setOtpError("Invalid OTP. Please try again.");
		} else if (result && result.result === "Expired") {
			setIsEmailValid(false);
			setUserEmailConfirmed(false);
			setOtpError("Expired OTP. Please try again.");
			setShowResendOtpButton(true);
		}
	};

	const clearUser = () => {
		setUserFirstName("");
		setUserLastName("");
		setUserEmailAddress("");
		setUserEmailConfirmed(false);
		setUserTermsAccepted(false);
		setUser(undefined);
		sessionStorage.removeItem("sc_calculator_user");
	};

	const handleUserDetailsSubmit = async (isResending?: boolean) => {
		console.log(userFirstName);
		setLoading(true);
		setShowResendOtpButton(false);
		setOtpError("");
		const result = await viewModel.current?.submitCalculatorUser(
			userFirstName,
			userLastName,
			userEmailAddress
		);

		console.log(`result`, result);

		if (result && result.result) {
			if (!result.result.isInvited) {
				setShowUnAuthenticatedUser(true);
				setLoading(false);
			} else {
				setLoading(false);
				setShowOtpEntryBox(true);
			}
		}
	};

	return (
		<div className="bg-gradient-to-t from-gray-700 to-gray-900 w-screen min-h-screen h-full flex items-center justify-center py-4">
			{!user && (
				<div className="p-12 bg-gradient-to-r from-white to-sky-300 shadow-lg w-full max-w-4xl mx-auto flex flex-col items-center justify-center gap-4">
					<Logo />
					<div className="flex flex-col items-center justify-center gap-1 w-full">
						<span className="font-Montserrat font-semibold text-xl text-center">
							Need to access the Secured Calls pricing calculator?
						</span>
						<span className="font-Montserrat font-semibold text-sm text-center">
							Please provide your information to get started.
						</span>
					</div>
					<InputBox
						label={"First Name"}
						type={"text"}
						value={userFirstName}
						onChange={function (value: string): void {
							setUserFirstName(value);
						}}
						required
					></InputBox>
					<InputBox
						label={"Last Name"}
						type={"text"}
						value={userLastName}
						onChange={function (value: string): void {
							setUserLastName(value);
						}}
						required
					></InputBox>
					<div className="flex items-center justify-center gap-4 w-full">
						<InputBox
							label="Email address"
							type="email"
							required
							value={userEmailAddress}
							onChange={(value: string, isValid?: boolean) => {
								setUserEmailAddress(value); // Update email state
								setIsEmailValid(isValid); // Track validity
								setUserEmailConfirmed(false);
							}}
						/>
					</div>
					<div className="flex items-center text-left w-full">
						<CheckBox
							onChange={function (value: boolean): void {
								setUserTermsAccepted(value);
							}}
							checked={userTermsAccepted}
						></CheckBox>
						<label
							htmlFor="link-checkbox"
							className="font-Poppins ms-2 text-sm font-semibold text-gray-900 "
						>
							I agree with the{" "}
							<HyperLink
								target="blank"
								href="#"
								onClick={toggleShowDisclaimer}
								label="terms and conditions."
							></HyperLink>
						</label>
					</div>

					<ButtonPro
						disabled={
							!userFirstName ||
							!userLastName ||
							!userTermsAccepted
						}
						onClick={handleUserDetailsSubmit}
						label={"Submit"}
					></ButtonPro>
				</div>
			)}
			{user && (
				<div className="p-6 bg-gradient-to-r from-white to-sky-300 shadow-lg rounded-md w-full max-w-4xl mx-auto flex flex-col items-center justify-center gap-6">
					<div className="flex items-center justify-end gap-1 w-full">
						<HyperLink
							onClick={clearUser}
							href={""}
							label={`Not ${user.firstName} ${user.lastName} ?`}
						></HyperLink>
						<span className="font-Montserrat font-semibold text-sm text-red-500"></span>
					</div>
					<div className="flex flex-col items-center justify-center gap-4 w-full mb-4 ">
						<Logo />

						<div className="flex items-center justify-center gap-4 w-full">
							<span className="font-Poppins text-center tracking-wider bg-gradient-to-bl from-purple-600 to-red-800 text-4xl md:text-6xl font-bold lg:text-4xl bg-clip-text text-transparent uppercase drop-shadow-2xl">
								Reseller - Pricing calculator
							</span>
							<button onClick={toggleShowDisclaimer}>
								<InformationCallIcon className="w-6 h-6 text-purple-600"></InformationCallIcon>
							</button>
						</div>
					</div>

					<div className="flex items-center justify-center gap-6 w-full">
						<BoxWithIcon
							icon={
								<OutgoingCallIcon className="w-4 h-4"></OutgoingCallIcon>
							}
							title={"Outgoing calls"}
						>
							<div className="flex flex-col items-center justify-normal gap-6">
								<InputBox
									label="Total outgoing calls/month"
									type="number"
									value={`${numOutgoingCalls}`}
									onChange={(value) =>
										setNumOutgoingCalls(
											Math.min(
												parseInt(value) || 0,
												1000000
											)
										)
									}
								/>
								<InputBox
									label="Avg outgoing call length (in minutes)"
									type="number"
									value={`${avgOutgoingCallLength}`}
									onChange={(value) =>
										setAvgOutgoingCallLength(
											Math.min(parseInt(value) || 0, 1440)
										)
									}
								/>
							</div>
						</BoxWithIcon>
						<BoxWithIcon
							icon={
								<IncomingCallIcon className="w-4 h-4"></IncomingCallIcon>
							}
							title={"Incoming calls"}
						>
							<div className="flex flex-col items-center justify-normal gap-6">
								<InputBox
									label="Total Incoming Calls / Month"
									type="number"
									value={`${numIncomingCalls}`}
									onChange={(value) =>
										setNumIncomingCalls(
											Math.min(
												parseInt(value) || 0,
												1000000
											)
										)
									}
								/>
								<InputBox
									label="Avg incoming call length (in minutes)"
									type="number"
									value={`${avgIncomingCallLength}`}
									onChange={(value) =>
										setAvgIncomingCallLength(
											Math.min(parseInt(value) || 0, 1440)
										)
									}
								/>
							</div>
						</BoxWithIcon>
					</div>
					{/* <div className="flex flex-col items-end justify-end gap-2 w-full">
					<Toggle
						labelLeft="USD"
						onChange={(checked) => {
							if (checked) {
								setApplicationLocalRates(true);
								setLocalRate(conversionRate);
							} else {
								setApplicationLocalRates(false);
								setLocalRate(1);
							}
						}}
						labelRight="AUD"
					></Toggle>
					<span className="font-Montserrat font-semibold text-sm">
						Current conversion rate{" :"}
						<NumberDisplay
							value={localRate}
							decimalPlaces={4}
						></NumberDisplay>
					</span>
				</div> */}
					{/* <span className="font-Montserrat font-semibold text-sm">
					Current conversion rate{" :"}
					<NumberDisplay
						value={conversionRate}
						decimalPlaces={4}
					></NumberDisplay>
				</span> */}
					<div className="flex flex-col items-center justify-center gap-12 w-full mt-4">
						<BoxWithIcon
							icon={<DollarIcon className="w-6 h-6"></DollarIcon>}
							title={"Secured calls costing"}
						>
							<div className="flex flex-col items-center justify-center gap-4 w-full">
								<div className="flex items-center justify-center gap-4 w-full">
									<div className="flex flex-col items-center justify-center gap-2 w-full border-2 border-dashed p-2">
										<span className="font-Montserrat font-semibold text-2xl text-center">
											Platform cost
										</span>
										<CheckBox
											title="24*7 support"
											checked={supportNeeded247}
											onChange={function (
												value: boolean
											): void {
												setSupportNeeded247(value);
											}}
										></CheckBox>
										<span className="font-Montserrat font-semibold text-xl text-center">
											<CurrencyDisplay
												currency={
													applyingLocalRates
														? "AUD"
														: "USD"
												}
												minimumFractionDigits={2}
												maximumFractionDigits={2}
												amount={
													platformCost * localRate
												}
											></CurrencyDisplay>{" "}
											/ month
										</span>
										<span className="font-Montserrat font-semibold text-center">
											{`Includes infrastructure cost and
										${supportNeeded247 ? "premium" : "standard"} support`}
										</span>
									</div>
								</div>
								<Divider></Divider>
								<div className="flex flex-col items-end justify-center gap-2 w-full">
									{/* Total Cost Breakdown */}
									<table className="w-full border shadow-md">
										<thead>
											<tr className="bg-gray-100 text-sm font-Montserrat py-24">
												<th className="border px-4 py-2">
													Cost Type
												</th>
												<th className="border px-4 py-2">
													Cost to reseller
												</th>
												<th className="border px-4 py-2">
													Reseller retail price @
												</th>
												<th className="border px-4 py-2">
													Reseller margin
												</th>
											</tr>
											<tr>
												<td></td>
												<td></td>
												<td className="font-Montserrat font-semibold text-right">
													<DropdownBox
														defaultText={"30%"}
														options={[
															{
																value: "0.95",
																label: "5% margin",
															},
															{
																value: "0.9",
																label: "10% margin",
															},
															{
																value: "0.85",
																label: "15% margin",
															},
															{
																value: "0.8",
																label: "20% margin",
															},
															{
																value: "0.75",
																label: "25% margin",
															},
															{
																value: "0.7",
																label: "30% margin",
															},
															{
																value: "0.65",
																label: "35% margin",
															},
															{
																value: "0.6",
																label: "40% margin",
															},
															{
																value: "0.55",
																label: "45% margin",
															},
															{
																value: "0.5",
																label: "50% margin",
															},
															{
																value: "0.45",
																label: "55% margin",
															},
															{
																value: "0.4",
																label: "60% margin",
															},
															{
																value: "0.35",
																label: "65% margin",
															},
															{
																value: "0.30",
																label: "70% margin",
															},
															{
																value: "0.25",
																label: "75% margin",
															},
														]}
														onSelected={function (
															selectedOption: IDropdownOptionProp
														): void {
															setResellerMarginMultiplier(
																parseFloat(
																	selectedOption.value
																) || 30
															);
														}}
													></DropdownBox>
												</td>
												<td></td>
											</tr>
										</thead>
										<tbody>
											<tr className="border bg-yellow-200">
												<td className="border px-4 py-1 font-Montserrat font-semibold">
													Platform cost
												</td>
												<td className="border font-bold font-Montserrat  text-lg text-right px-2">
													<CurrencyDisplay
														currency={
															applyingLocalRates
																? "AUD"
																: "USD"
														}
														minimumFractionDigits={
															2
														}
														maximumFractionDigits={
															2
														}
														amount={
															platformCost *
															localRate
														}
													></CurrencyDisplay>
												</td>
												<td className="border font-bold font-Montserrat  text-lg text-right px-2">
													<CurrencyDisplay
														currency={
															applyingLocalRates
																? "AUD"
																: "USD"
														}
														minimumFractionDigits={
															2
														}
														maximumFractionDigits={
															2
														}
														amount={
															(platformCost *
																localRate) /
															resellerMarginMultiplier
														}
													></CurrencyDisplay>
												</td>
												<td className="border font-bold font-Montserrat  text-lg text-right px-2">
													<CurrencyDisplay
														currency={
															applyingLocalRates
																? "AUD"
																: "USD"
														}
														amount={
															(platformCost *
																localRate) /
																resellerMarginMultiplier -
															platformCost *
																localRate
														}
													></CurrencyDisplay>
												</td>
											</tr>
											<tr className="border bg-red-200">
												<td className="border px-4 py-1 font-Montserrat font-medium flex items-center">
													<div className="flex items-center justify-start gap-2 w-full">
														<span className="font-Montserrat font-semibold">
															Branding cost
														</span>
														{brandingBreakdown.length >
															0 && (
															<button
																className="mr-2 p-1 border rounded hover:bg-gray-300"
																onClick={() =>
																	setIsBrandingExpanded(
																		!isBrandingExpanded
																	)
																}
															>
																{isBrandingExpanded ? (
																	<MinusIcon className="w-4 h-4 text-gray-700" />
																) : (
																	<PlusIcon className="w-4 h-4 text-gray-700" />
																)}
															</button>
														)}
													</div>
												</td>
												<td className="border font-bold font-Montserrat  text-lg text-right px-2">
													<CurrencyDisplay
														currency={
															applyingLocalRates
																? "AUD"
																: "USD"
														}
														amount={brandingCost}
													></CurrencyDisplay>
												</td>
												<td className="border font-bold font-Montserrat  text-lg text-right px-2">
													<CurrencyDisplay
														currency={
															applyingLocalRates
																? "AUD"
																: "USD"
														}
														amount={
															brandingCost /
															resellerMarginMultiplier
														}
													></CurrencyDisplay>
												</td>
												<td className="border font-bold font-Montserrat  text-lg text-right px-2">
													<CurrencyDisplay
														currency={
															applyingLocalRates
																? "AUD"
																: "USD"
														}
														amount={
															brandingCost /
																resellerMarginMultiplier -
															brandingCost
														}
													></CurrencyDisplay>
												</td>
											</tr>

											{/* Branding Breakdown Table - Conditional Rendering */}
											{isBrandingExpanded &&
												brandingBreakdown.length >
													0 && (
													<tr className="border bg-rose-100">
														<td
															colSpan={4}
															className="border p-4"
														>
															<table className="w-full border-collapse border border-gray-300">
																<thead>
																	<tr className="bg-gray-100">
																		<th className="border border-gray-300 px-4 py-1">
																			Tier
																		</th>
																		<th className="border border-gray-300 px-4 py-1">
																			Calls
																		</th>
																		<th className="border border-gray-300 px-4 py-1">
																			Cost
																			to
																			reseller
																		</th>
																		<th className="border border-gray-300 px-4 py-1">
																			Reseller
																			retail
																			price
																		</th>
																	</tr>
																</thead>
																<tbody>
																	{brandingBreakdown.map(
																		(
																			tier,
																			index
																		) => (
																			<tr
																				key={
																					index
																				}
																				className="bg-gray-50"
																			>
																				<td className="border border-gray-300 px-4 py-1 font-Montserrat font-semibold">
																					{
																						tier.tier
																					}
																				</td>
																				<td className="border border-gray-300 px-4 py-1 text-right text-xl font-code font-semibold">
																					{
																						tier.description
																					}
																				</td>
																				<td className="border border-gray-300 px-4 py-1 text-right">
																					<CurrencyDisplay
																						currency={
																							applyingLocalRates
																								? "AUD"
																								: "USD"
																						}
																						amount={parseFloat(
																							tier.cost
																						)}
																						minimumFractionDigits={
																							4
																						}
																						maximumFractionDigits={
																							4
																						}
																					></CurrencyDisplay>
																				</td>
																				<td className="border border-gray-300 px-4 py-1 text-right">
																					<CurrencyDisplay
																						currency={
																							applyingLocalRates
																								? "AUD"
																								: "USD"
																						}
																						amount={
																							parseFloat(
																								tier.cost
																							) /
																							resellerMarginMultiplier
																						}
																						minimumFractionDigits={
																							4
																						}
																						maximumFractionDigits={
																							4
																						}
																					></CurrencyDisplay>
																				</td>
																			</tr>
																		)
																	)}
																</tbody>
															</table>
														</td>
													</tr>
												)}

											<tr className="border bg-teal-200">
												<td className="border px-4 py-1 font-Montserrat font-medium">
													<div className="flex items-center justify-start gap-2 w-full">
														<span className="font-Montserrat font-semibold">
															Outgoing call cost
														</span>
														{callBreakdown.length >
															0 && (
															<button
																className="mr-2 p-1 border rounded hover:bg-gray-300"
																onClick={() =>
																	setIsOutgoingCallCostBreakdownExpanded(
																		!isOutgoingCallCostBreakdownExpanded
																	)
																}
															>
																{isOutgoingCallCostBreakdownExpanded ? (
																	<MinusIcon className="w-4 h-4 text-gray-700" />
																) : (
																	<PlusIcon className="w-4 h-4 text-gray-700" />
																)}
															</button>
														)}
													</div>
												</td>
												<td className="border font-bold font-Montserrat  text-lg text-right px-2">
													<CurrencyDisplay
														currency={
															applyingLocalRates
																? "AUD"
																: "USD"
														}
														amount={
															outgoingCallCost
														}
													></CurrencyDisplay>
												</td>
												<td className="border font-bold font-Montserrat  text-lg text-right px-2">
													<CurrencyDisplay
														currency={
															applyingLocalRates
																? "AUD"
																: "USD"
														}
														amount={
															outgoingCallCost /
															resellerMarginMultiplier
														}
													></CurrencyDisplay>
												</td>
												<td className="border font-bold font-Montserrat  text-lg text-right px-2">
													<CurrencyDisplay
														currency={
															applyingLocalRates
																? "AUD"
																: "USD"
														}
														amount={
															outgoingCallCost /
																resellerMarginMultiplier -
															outgoingCallCost
														}
													></CurrencyDisplay>
												</td>
											</tr>
											{isOutgoingCallCostBreakdownExpanded &&
												callBreakdown.filter(
													(p) =>
														p.direction ===
														"Outgoing"
												).length > 0 && (
													<tr className="border bg-teal-100">
														<td
															colSpan={4}
															className="border p-4"
														>
															<table className="w-full border-collapse border border-gray-300">
																<thead>
																	<tr className="bg-gray-100">
																		<th className="border border-gray-300 px-4 py-1">
																			Tier
																		</th>
																		<th className="border border-gray-300 px-4 py-1">
																			Call
																			mins
																			(
																			<NumberDisplay
																				value={
																					numOutgoingCalls *
																					avgOutgoingCallLength
																				}
																			></NumberDisplay>
																			)
																		</th>
																		<th className="border border-gray-300 px-4 py-1">
																			Cost
																			to
																			reseller
																		</th>
																		<th className="border border-gray-300 px-4 py-1">
																			Reseller
																			retail
																			price
																		</th>
																	</tr>
																</thead>
																<tbody>
																	{callBreakdown
																		.filter(
																			(
																				p
																			) =>
																				p.direction ===
																				"Outgoing"
																		)
																		.map(
																			(
																				tier,
																				index
																			) => (
																				<tr
																					key={
																						index
																					}
																					className="bg-gray-50"
																				>
																					<td className="border border-gray-300 px-4 py-1 font-Montserrat font-semibold">
																						{
																							tier.tier
																						}
																					</td>
																					<td className="border border-gray-300 px-4 py-1 text-right text-xl font-code font-semibold">
																						{
																							tier.description
																						}
																					</td>
																					<td className="border border-gray-300 px-4 py-1 text-right">
																						<CurrencyDisplay
																							currency={
																								applyingLocalRates
																									? "AUD"
																									: "USD"
																							}
																							amount={parseFloat(
																								tier.cost
																							)}
																							minimumFractionDigits={
																								4
																							}
																							maximumFractionDigits={
																								4
																							}
																						></CurrencyDisplay>
																					</td>
																					<td className="border border-gray-300 px-4 py-1 text-right">
																						<CurrencyDisplay
																							currency={
																								applyingLocalRates
																									? "AUD"
																									: "USD"
																							}
																							amount={
																								parseFloat(
																									tier.cost
																								) /
																								resellerMarginMultiplier
																							}
																							minimumFractionDigits={
																								4
																							}
																							maximumFractionDigits={
																								4
																							}
																						></CurrencyDisplay>
																					</td>
																				</tr>
																			)
																		)}
																</tbody>
															</table>
														</td>
													</tr>
												)}
											<tr className="border bg-orange-200">
												<td className="border px-4 py-1 font-Montserrat font-medium">
													<div className="flex items-center justify-start gap-2 w-full">
														<span className="font-Montserrat font-semibold">
															Incoming call cost
														</span>
														{callBreakdown.filter(
															(p) =>
																p.direction ===
																"Incoming"
														).length > 0 && (
															<button
																className="mr-2 p-1 border rounded hover:bg-gray-300"
																onClick={() =>
																	setIsIncomingCallCostBreakdownExpanded(
																		!isIncomingCallCostBreakdownExpanded
																	)
																}
															>
																{isIncomingCallCostBreakdownExpanded ? (
																	<MinusIcon className="w-4 h-4 text-gray-700" />
																) : (
																	<PlusIcon className="w-4 h-4 text-gray-700" />
																)}
															</button>
														)}
													</div>
												</td>
												<td className="border font-bold font-Montserrat  text-lg text-right px-2">
													<CurrencyDisplay
														currency={
															applyingLocalRates
																? "AUD"
																: "USD"
														}
														amount={
															incomingCallCost
														}
													></CurrencyDisplay>
												</td>
												<td className="border font-bold font-Montserrat  text-lg text-right px-2">
													<CurrencyDisplay
														currency={
															applyingLocalRates
																? "AUD"
																: "USD"
														}
														amount={
															incomingCallCost /
															resellerMarginMultiplier
														}
													></CurrencyDisplay>
												</td>

												<td className="border font-bold font-Montserrat  text-lg text-right px-2">
													<CurrencyDisplay
														currency={
															applyingLocalRates
																? "AUD"
																: "USD"
														}
														amount={
															incomingCallCost /
																resellerMarginMultiplier -
															incomingCallCost
														}
													></CurrencyDisplay>
												</td>
											</tr>

											{/* <tr className="border">
											<td className="border px-4 py-1 font-Montserrat font-medium">
												<div className="flex items-center justify-start gap-2 w-full">
													<span>
														Call cost breakdown
													</span>
													{callBreakdown.length >
														0 && (
														<button
															className="mr-2 p-1 border rounded hover:bg-gray-300"
															onClick={() =>
																setIsCallCostBreakdownExpanded(
																	!isCallCostBreakdownExpanded
																)
															}
														>
															{isCallCostBreakdownExpanded ? (
																<MinusIcon className="w-4 h-4 text-gray-700" />
															) : (
																<PlusIcon className="w-4 h-4 text-gray-700" />
															)}
														</button>
													)}
												</div>
											</td>
											<td className="border font-bold font-Montserrat  text-lg text-right px-2"></td>
											<td className="border font-bold font-Montserrat  text-lg text-right px-2"></td>

											<td className="border font-bold font-Montserrat  text-lg text-right px-2"></td>
										</tr> */}
											{isIncomingCallCostBreakdownExpanded &&
												callBreakdown.length > 0 && (
													<tr className="border bg-orange-100">
														<td
															colSpan={4}
															className="border p-4"
														>
															<table className="w-full border-collapse border border-gray-300">
																<thead>
																	<tr className="bg-gray-100">
																		<th className="border border-gray-300 px-4 py-1">
																			Tier
																		</th>
																		<th className="border border-gray-300 px-4 py-1">
																			Call
																			mins
																			(
																			<NumberDisplay
																				value={
																					numIncomingCalls *
																					avgIncomingCallLength
																				}
																			></NumberDisplay>
																			)
																		</th>
																		<th className="border border-gray-300 px-4 py-1">
																			Cost
																			to
																			reseller
																		</th>
																		<th className="border border-gray-300 px-4 py-1">
																			Reseller
																			retail
																			price
																		</th>
																	</tr>
																</thead>
																<tbody>
																	{callBreakdown
																		.filter(
																			(
																				p
																			) =>
																				p.direction ===
																				"Incoming"
																		)
																		.map(
																			(
																				tier,
																				index
																			) => (
																				<tr
																					key={
																						index
																					}
																					className="bg-gray-50"
																				>
																					<td className="border border-gray-300 px-4 py-1 font-Montserrat font-semibold">
																						{
																							tier.tier
																						}
																					</td>
																					<td className="border border-gray-300 px-4 py-1 text-right text-xl font-code font-semibold">
																						{
																							tier.description
																						}
																					</td>
																					<td className="border border-gray-300 px-4 py-1 text-right">
																						<CurrencyDisplay
																							currency={
																								applyingLocalRates
																									? "AUD"
																									: "USD"
																							}
																							amount={parseFloat(
																								tier.cost
																							)}
																							minimumFractionDigits={
																								4
																							}
																							maximumFractionDigits={
																								4
																							}
																						></CurrencyDisplay>
																					</td>
																					<td className="border border-gray-300 px-4 py-1 text-right">
																						<CurrencyDisplay
																							currency={
																								applyingLocalRates
																									? "AUD"
																									: "USD"
																							}
																							amount={
																								parseFloat(
																									tier.cost
																								) /
																								resellerMarginMultiplier
																							}
																							minimumFractionDigits={
																								4
																							}
																							maximumFractionDigits={
																								4
																							}
																						></CurrencyDisplay>
																					</td>
																				</tr>
																			)
																		)}
																</tbody>
															</table>
														</td>
													</tr>
												)}
											<tr>
												<td className="border px-4 py-1 font-bold">
													Total per month
												</td>
												<td className="border font-bold font-Montserrat text-lg text-right px-2">
													<CurrencyDisplay
														currency={
															applyingLocalRates
																? "AUD"
																: "USD"
														}
														amount={monthlyCost}
													></CurrencyDisplay>
												</td>
												<td className="border font-bold font-Montserrat text-lg text-right px-2">
													<CurrencyDisplay
														currency={
															applyingLocalRates
																? "AUD"
																: "USD"
														}
														amount={
															monthlyCost /
															resellerMarginMultiplier
														}
													></CurrencyDisplay>
												</td>
												<td className="border font-bold font-Montserrat text-lg text-right px-2">
													<CurrencyDisplay
														currency={
															applyingLocalRates
																? "AUD"
																: "USD"
														}
														amount={
															monthlyCost /
																resellerMarginMultiplier -
															monthlyCost
														}
													></CurrencyDisplay>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>
						</BoxWithIcon>
						<div className="border-t-2 border-dashed border-teal-600 w-full drop-shadow-lg"></div>
						<BoxWithIcon
							icon={<DollarIcon className="w-6 h-6"></DollarIcon>}
							title={"Compared to other providers"}
						>
							<div className="bg-gradient-to-r from-orange-100 to-orange-200 flex flex-col items-start justify-center gap-2 p-4">
								<span className="font-Montserrat font-semibold ">
									Total of Secured calls cost to customer
								</span>
								<CurrencyDisplay
									currency={
										applyingLocalRates ? "AUD" : "USD"
									}
									amount={
										monthlyCost / resellerMarginMultiplier
									}
								></CurrencyDisplay>
								<span className="font-Montserrat font-semibold ">
									Total of other provider cost
								</span>
								<CurrencyDisplay
									currency={
										applyingLocalRates ? "AUD" : "USD"
									}
									amount={
										((percentageOfMobileTo1300CallMinutes *
											(numIncomingCalls *
												avgIncomingCallLength)) /
											100) *
											mobileTo1300CallRates +
										((percentageOfLandlineTo1300CallMinutes *
											(numIncomingCalls *
												avgIncomingCallLength)) /
											100) *
											landlineTo1300CallRates +
										(((percentageOfCallToMobile *
											(numOutgoingCalls *
												avgOutgoingCallLength)) /
											100) *
											callRatesToMobile +
											((percentageOfCallToLandline *
												(numOutgoingCalls *
													avgOutgoingCallLength)) /
												100) *
												callRatesToLandline +
											(monthlyCostForSIPAndInfra +
												monthlyChannelCost))
									}
									minimumFractionDigits={2}
									maximumFractionDigits={2}
								></CurrencyDisplay>
							</div>
						</BoxWithIcon>
						<BoxWithIcon
							icon={<DollarIcon className="w-6 h-6"></DollarIcon>}
							title={"Cost compare for 1300"}
						>
							<div className="flex flex-col items-center justify-center gap-4 w-full">
								<div className="flex items-center justify-start gap-4 w-full mb-4">
									<span className="font-Montserrat font-semibold text-lg">
										You have total of
										<NumberDisplay
											value={
												numIncomingCalls *
												avgIncomingCallLength
											}
										></NumberDisplay>
										incoming call minutes
									</span>
								</div>
								<div className="flex items-center justify-center gap-4 w-full">
									<InputBox
										label={
											"% of 1300 call from Australian mobile"
										}
										type={"number"}
										value={`${percentageOfMobileTo1300CallMinutes}`}
										onChange={(value) =>
											handleMobileChange(
												parseFloat(value) || 0
											)
										}
									></InputBox>
									<InputBox
										label={"Calling rates in cents/call"}
										type={"text"} // Use text to prevent browser restrictions
										value={mobileTo1300CallRatesInputValue} // Bind input to temporary string state
										onChange={(value: string) => {
											// Allow valid decimal input
											if (/^\d*\.?\d*$/.test(value)) {
												setMobileTo1300CallRatesInputValue(
													value
												); // Update input field
												setMobileTo1300CallRates(
													value === ""
														? 0
														: parseFloat(value)
												); // Convert only when needed
											}
										}}
									></InputBox>
								</div>
								<div className="flex items-center justify-center gap-4 w-full">
									<InputBox
										label={
											"% of 1300 call from Australian landline"
										}
										type={"number"}
										value={`${percentageOfLandlineTo1300CallMinutes}`}
										onChange={(value) =>
											handleLandlineChange(
												parseFloat(value) || 0
											)
										}
									></InputBox>
									<InputBox
										label={"Calling rates in cents/call"}
										type={"text"} // Use text to prevent browser restrictions
										value={
											landlineTo1300CallRatesInputValue
										} // Bind input to temporary string state
										onChange={(value: string) => {
											// Allow valid decimal input
											if (/^\d*\.?\d*$/.test(value)) {
												setLandlineTo1300CallRatesInputValue(
													value
												); // Update input field
												setLandlineTo1300CallRates(
													value === ""
														? 0
														: parseFloat(value)
												); // Convert only when needed
											}
										}}
									></InputBox>
								</div>

								<table className="w-full border-collapse border border-gray-300 mt-4">
									<thead>
										<tr className="bg-gray-100">
											<th className="border border-gray-300 px-4 py-1 text-center font-Montserrat font-semibold">
												Call Type
											</th>
											<th className="border border-gray-300 px-4 py-1 text-center font-Montserrat font-semibold">
												Cost
											</th>
										</tr>
									</thead>
									<tbody>
										<tr className="bg-gray-50">
											<td className="border border-gray-300 px-4 py-2 font-Montserrat font-semibold">
												Mobile to 1300 number
											</td>
											<td className="border font-bold font-Montserrat text-lg text-right px-2">
												<CurrencyDisplay
													currency={
														applyingLocalRates
															? "AUD"
															: "USD"
													}
													amount={
														((percentageOfMobileTo1300CallMinutes *
															(numIncomingCalls *
																avgIncomingCallLength)) /
															100) *
														mobileTo1300CallRates
													}
													minimumFractionDigits={2}
													maximumFractionDigits={2}
												></CurrencyDisplay>
											</td>
										</tr>
										<tr className="bg-gray-50">
											<td className="border border-gray-300 px-4 py-2 font-Montserrat font-semibold">
												Landline to 1300 number
											</td>
											<td className="border font-bold font-Montserrat text-lg text-right px-2">
												<CurrencyDisplay
													currency={
														applyingLocalRates
															? "AUD"
															: "USD"
													}
													amount={
														((percentageOfLandlineTo1300CallMinutes *
															(numIncomingCalls *
																avgIncomingCallLength)) /
															100) *
														landlineTo1300CallRates
													}
													minimumFractionDigits={2}
													maximumFractionDigits={2}
												></CurrencyDisplay>
											</td>
										</tr>

										<tr className="bg-gray-50">
											<td className="border border-gray-300 px-4 py-2 font-Montserrat font-semibold">
												Total cost
											</td>
											<td className="border font-bold font-Montserrat text-lg text-right px-2">
												<CurrencyDisplay
													currency={
														applyingLocalRates
															? "AUD"
															: "USD"
													}
													amount={
														(((percentageOfMobileTo1300CallMinutes *
															(numIncomingCalls *
																avgIncomingCallLength)) /
															100) *
															mobileTo1300CallRates +
															((percentageOfLandlineTo1300CallMinutes *
																(numIncomingCalls *
																	avgIncomingCallLength)) /
																100) *
																landlineTo1300CallRates) *
														localRate
													}
													minimumFractionDigits={2}
													maximumFractionDigits={2}
												></CurrencyDisplay>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</BoxWithIcon>
						<BoxWithIcon
							icon={<DollarIcon className="w-6 h-6"></DollarIcon>}
							title={"Cost compare outbound calls"}
						>
							<div className="flex flex-col items-center justify-center gap-4 w-full">
								<div className="flex items-center justify-start gap-4 w-full mb-4">
									<span className="font-Montserrat font-semibold text-lg">
										You have total of
										<NumberDisplay
											value={
												numOutgoingCalls *
												avgOutgoingCallLength
											}
										></NumberDisplay>
										outgoing call minutes
									</span>
								</div>
								<div className="flex items-center justify-center gap-4 w-full">
									<InputBox
										label={
											"% of calls to Australian mobile"
										}
										type={"number"}
										value={`${percentageOfCallToMobile}`}
										onChange={(value) =>
											handleOutboundMobileChange(
												parseFloat(value) || 0
											)
										}
									></InputBox>
									<InputBox
										label={"Calling rates in cents/call"}
										type={"text"} // Use text to prevent browser restrictions
										value={callRatesToMobileInputValue} // Bind input to temporary string state
										onChange={(value: string) => {
											// Allow valid decimal input
											if (/^\d*\.?\d*$/.test(value)) {
												setCallRatesToMobileInputValue(
													value
												); // Update input field
												setCallRatesToMobile(
													value === ""
														? 0
														: parseFloat(value)
												); // Convert only when needed
											}
										}}
									></InputBox>
								</div>
								<div className="flex items-center justify-center gap-4 w-full">
									<InputBox
										label={
											"% of calls to Australian landline"
										}
										type={"number"}
										value={`${percentageOfCallToLandline}`}
										onChange={(value) =>
											handleOutboundLandlineChange(
												parseFloat(value) || 0
											)
										}
									></InputBox>
									<InputBox
										label={"Calling rates in cents/call"}
										type={"text"} // Use text to prevent browser restrictions
										value={callRatesToLandlineInputValue} // Bind input to temporary string state
										onChange={(value: string) => {
											// Allow valid decimal input
											if (/^\d*\.?\d*$/.test(value)) {
												setCallRatesToLandlineInputValue(
													value
												); // Update input field
												setCallRatesToLandline(
													value === ""
														? 0
														: parseFloat(value)
												); // Convert only when needed
											}
										}}
									></InputBox>
								</div>
								<div className="flex items-center justify-center gap-4 w-full">
									<InputBox
										label={
											"Monthly fixed SIP and Infra cost"
										}
										type={"number"}
										value={`${monthlyCostForSIPAndInfra}`}
										onChange={(value) =>
											setMonthlyCostForSIPAndInfra(
												parseFloat(value) || 0
											)
										}
									></InputBox>
									<InputBox
										label={"Monthly fixed channel cost"}
										type={"number"} // Use text to prevent browser restrictions
										value={`${monthlyChannelCost}`}
										onChange={(value) =>
											setMonthlyChannelCost(
												parseFloat(value) || 0
											)
										}
									></InputBox>
								</div>
								<table className="w-full border-collapse border border-gray-300 mt-4">
									<thead>
										<tr className="bg-gray-100">
											<th className="border border-gray-300 px-4 py-1 text-center font-Montserrat font-semibold">
												Call Type
											</th>
											<th className="border border-gray-300 px-4 py-1 text-center font-Montserrat font-semibold">
												Cost
											</th>
										</tr>
									</thead>
									<tbody>
										<tr className="bg-gray-50">
											<td className="border border-gray-300 px-4 py-2 font-Montserrat font-semibold">
												Outbound calls to Mobile
											</td>
											<td className="border font-bold font-Montserrat text-lg text-right px-2">
												<CurrencyDisplay
													currency={
														applyingLocalRates
															? "AUD"
															: "USD"
													}
													amount={
														((percentageOfCallToMobile *
															(numOutgoingCalls *
																avgOutgoingCallLength)) /
															100) *
														callRatesToMobile
													}
												></CurrencyDisplay>
											</td>
										</tr>
										<tr className="bg-gray-50">
											<td className="border border-gray-300 px-4 py-2 font-Montserrat font-semibold">
												Outbound calls to landline
											</td>
											<td className="border font-bold font-Montserrat text-lg text-right px-2">
												<CurrencyDisplay
													currency={
														applyingLocalRates
															? "AUD"
															: "USD"
													}
													amount={
														((percentageOfCallToLandline *
															(numOutgoingCalls *
																avgOutgoingCallLength)) /
															100) *
														callRatesToLandline
													}
												></CurrencyDisplay>
											</td>
										</tr>
										<tr className="bg-gray-50">
											<td className="border border-gray-300 px-4 py-2 font-Montserrat font-semibold">
												Total fixed cost
											</td>
											<td className="border font-bold font-Montserrat text-lg text-right px-2">
												<CurrencyDisplay
													currency={
														applyingLocalRates
															? "AUD"
															: "USD"
													}
													amount={
														monthlyCostForSIPAndInfra +
														monthlyChannelCost
													}
												></CurrencyDisplay>
											</td>
										</tr>
										<tr className="bg-gray-50">
											<td className="border border-gray-300 px-4 py-2 font-Montserrat font-semibold">
												Total cost
											</td>
											<td className="border font-bold font-Montserrat text-lg text-right px-2">
												<CurrencyDisplay
													currency={
														applyingLocalRates
															? "AUD"
															: "USD"
													}
													amount={
														((percentageOfCallToMobile *
															(numOutgoingCalls *
																avgOutgoingCallLength)) /
															100) *
															callRatesToMobile +
														((percentageOfCallToLandline *
															(numOutgoingCalls *
																avgOutgoingCallLength)) /
															100) *
															callRatesToLandline +
														(monthlyCostForSIPAndInfra +
															monthlyChannelCost)
													}
												></CurrencyDisplay>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</BoxWithIcon>
					</div>
					<CopyRight textColor="text-gray-800" />
				</div>
			)}
			<LoadingModal open={loading}></LoadingModal>
			<Modal
				isOpen={showOtpEntryBox}
				buttonDisabled={otpConfirmButtonDisabled}
				buttonText="Confirm"
				onClose={() => {
					if (otp) {
						confirmUserEmail();
					}
				}}
			>
				<span className="font-Poppins font-semibold">
					Enter the OTP sent to your email address
				</span>
				<OTPInput
					numDigits={6}
					onComplete={function (otp: string): void {
						setOtpConfirmButtonDisabled(otp.length !== 6);
						setOtp(otp);
					}}
					disabled={showResendOtpButton}
				></OTPInput>
				{!showResendOtpButton && (
					<CountdownTimer
						duration={300}
						onElapsed={function (): void {
							setOtpError("");
							setShowResendOtpButton(true);
						}}
					></CountdownTimer>
				)}
				{otpError && <Alert text={otpError} type={"error"}></Alert>}
				{showResendOtpButton && (
					<HyperLink
						href={"#"}
						onClick={async () => {
							await handleUserDetailsSubmit(true);
						}}
						label={"Resend"}
					></HyperLink>
				)}
			</Modal>
			<Modal
				isOpen={showDisclaimer}
				onClose={function (): void {
					setShowDisclaimer(false);
				}}
				buttonText="I agree"
			>
				<div className="flex flex-col items-center justify-center gap-4">
					<Logo size={60}></Logo>
					<span className="font-Montserrat font-semibold text-2xl">
						Terms of Use
					</span>
					<span className="font-Montserrat font-semibold text-xl bg-sky-200 text-gray-800 p-1">
						Secured Calls Pricing Calculator
					</span>
					<p className="font-Montserrat font-normal text-center">
						The <strong>Secured Calls Pricing Calculator</strong> is
						designed exclusively for authorised resellers and
						potential partners exploring collaboration with{" "}
						<strong>Secured Calls</strong>. The calculated prices
						are for informational and estimation purposes only and
						do not constitute a final offer, contract, or legally
						binding agreement. Prices, terms, and conditions are
						subject to change without prior notice. Final pricing
						and partnership terms will be determined and confirmed
						upon successful approval as an authorized reseller.
					</p>
					<p className="font-semibold text-xl">Usage Restrictions</p>
					<ul className="list-decimal list-inside text-left mt-2">
						<li>Access is granted for evaluation purposes only.</li>
						<li>
							Unauthorized sharing, reproduction, or misuse of
							this tool or its output is strictly prohibited.
						</li>
						<li>
							Secured Calls reserves the right to monitor, audit,
							and revoke access at any time without notice.
						</li>
					</ul>
					<p className="font-semibold text-xl">
						Acknowledgment & Agreement
					</p>
					<p className="font-Montserrat font-normal text-center">
						By clicking <strong>“I Agree”</strong>, you confirm
						that: • You have read, understood, and agreed to these
						Terms of Use. • You acknowledge that the pricing
						calculator is a non-binding estimation tool.
					</p>
				</div>
			</Modal>
			<Modal
				isOpen={showUnAuthenticatedUser}
				onClose={function (): void {
					setShowUnAuthenticatedUser(false);
				}}
			>
				<span className="font-Poppins font-semibold">Unauthorised</span>
				<Alert
					text={
						"You are not authorised to use the calculator, Please get in touch with the Secured Calls team to get access."
					}
					type={"error"}
				></Alert>
				<HyperLink
					href={"mailto:support@securedcalls.com"}
					label={"Email us"}
				></HyperLink>
			</Modal>
		</div>
	);
};

export default PartnerPricingCalculator;
